import axiosInstance from '../../axios/axiosInstance'
import { PracticeWord, WriteSpeakState, WriteSpeakSubmitPayload, WriteSpeakSubmitResult } from 'types/writeSpeakTypes'
import { TranscribeGrant } from 'utils/liveTranscription'

// Writing & Speaking step. Reads fail soft (the step still works without them);
// submit rethrows so the send area can show what went wrong.

const qs = (storyId: string, level: string) =>
  `storyId=${encodeURIComponent(storyId)}&level=${encodeURIComponent(level?.toLowerCase() || '')}`

export const getWriteSpeakState = async (storyId: string, level: string): Promise<WriteSpeakState | null> => {
  try {
    const res = await axiosInstance.get(`writing-speaking/state?${qs(storyId, level)}`)
    return res?.data && typeof res.data.tier === 'string' ? res.data : null
  } catch (error) {
    return null
  }
}

// The backend's pick is deterministic but its inputs (mastery, look-ups) move,
// so the first list a learner sees for a story is kept on the device for a week
// and reused on reload. The list never reshuffles mid-task, and what gets sent
// as practiceWords is exactly what was shown.
const PRACTICE_WORDS_TTL_MS = 7 * 24 * 60 * 60 * 1000
const practiceWordsKey = (memberId: string, storyId: string) => `ws_practice_words_${memberId}_${storyId}`

const readCachedPracticeWords = (memberId: string, storyId: string): PracticeWord[] | null => {
  try {
    const raw = localStorage.getItem(practiceWordsKey(memberId, storyId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || !Array.isArray(parsed.words) || typeof parsed.savedAt !== 'number') return null
    if (Date.now() - parsed.savedAt > PRACTICE_WORDS_TTL_MS) {
      localStorage.removeItem(practiceWordsKey(memberId, storyId))
      return null
    }
    return parsed.words
  } catch {
    return null
  }
}

const writeCachedPracticeWords = (memberId: string, storyId: string, words: PracticeWord[]) => {
  try {
    localStorage.setItem(practiceWordsKey(memberId, storyId), JSON.stringify({ savedAt: Date.now(), words }))
  } catch {
    /* storage full or blocked: the list just won't survive a reload */
  }
}

export const getPracticeWords = async (storyId: string, level: string, memberId?: string): Promise<PracticeWord[]> => {
  if (memberId) {
    const cached = readCachedPracticeWords(memberId, storyId)
    if (cached) return cached
  }
  try {
    const res = await axiosInstance.get(`writing-speaking/practice-words?${qs(storyId, level)}`)
    const words: PracticeWord[] = Array.isArray(res?.data?.words)
      ? res.data.words.filter((w: any) => w && typeof w.word === 'string' && w.word.length > 0)
      : []
    // An empty list isn't pinned: it may only mean the story's words weren't ready yet.
    if (memberId && words.length) writeCachedPracticeWords(memberId, storyId, words)
    return words
  } catch (error) {
    return []
  }
}

export const submitWriteSpeak = async (payload: WriteSpeakSubmitPayload): Promise<WriteSpeakSubmitResult> => {
  const res = await axiosInstance.post(`writing-speaking/submit`, payload)
  return res?.data || { sent: false }
}

export const requestTranscribeToken = async (): Promise<TranscribeGrant> => {
  const res = await axiosInstance.post(`writing-speaking/transcribe-token`)
  return res?.data
}

export const reportTranscribeUsage = async (seconds: number) => {
  const clamped = Math.max(0, Math.min(600, Math.round(seconds)))
  return axiosInstance.post(`writing-speaking/transcribe-usage`, { seconds: clamped })
}
